import React from 'react';
import { doodleCatalog, familiesForTone, type DoodleCategory, type SurfaceTone } from '../../data/doodleShapes';

/**
 * Scatters supplied BCM shapes into the EMPTY areas of a section, so the site
 * feels playful and hand-made without ever sitting on top of text.
 *
 * How placement stays clear of content:
 * - Shapes are positioned inside a wrapper that mirrors `Container`
 *   (`mx-auto max-w-6xl px-5 sm:px-8`), so their coordinates line up with the
 *   real content box instead of the full viewport.
 * - Each section declares the SHAPE of its content with `layout`, and shapes are
 *   only placed in the negative space that layout leaves behind.
 * - Anything that relies on a gutter existing is hidden until the breakpoint
 *   where that gutter actually appears.
 *
 * Other guarantees:
 * - A shape's color family is never the same as the section background, so a
 *   blue shape can never disappear into a blue panel.
 * - Every shape is decorative: aria-hidden, no pointer events, no tab stop.
 * - Selection is deterministic from `seed`, so a section always renders the same
 *   arrangement while different sections get genuinely different shapes.
 */

/** Where a section's content sits, which tells us where the free space is. */
export type ContentLayout = /** Content fills the container (grids, card rows). Free: padding bands + outer gutters. */
'full'
/** Content is a centered narrow column. Free: the column's side gutters. */ | 'narrow'
/** Content hugs the left. Free: the whole right side. */ | 'leftColumn';
type Visibility = 'always' | 'sm' | 'lg' | 'xl';
type Zone = {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  rotate: number;
  size: Record<DoodleCategory, string>;
  /** Smallest breakpoint at which this zone is genuinely empty. */
  vis: Visibility;
};
const small: Record<DoodleCategory, string> = {
  stars: 'h-6 w-6 sm:h-8 sm:w-8',
  organics: 'h-7 w-7 sm:h-10 sm:w-10',
  strokes: 'h-3 w-14 sm:h-4 sm:w-20'
};
const medium: Record<DoodleCategory, string> = {
  stars: 'h-8 w-8 sm:h-11 sm:w-11',
  organics: 'h-10 w-10 sm:h-14 sm:w-14',
  strokes: 'h-4 w-20 sm:h-5 sm:w-28'
};
const large: Record<DoodleCategory, string> = {
  stars: 'h-10 w-10 sm:h-14 sm:w-14',
  organics: 'h-12 w-12 sm:h-20 sm:w-20',
  strokes: 'h-5 w-24 sm:h-6 sm:w-36'
};
const visibilityClass: Record<Visibility, string> = {
  always: '',
  sm: 'hidden sm:block',
  lg: 'hidden lg:block',
  xl: 'hidden xl:block'
};

/**
 * Full-width content: the only reliably empty space is the section's own top and
 * bottom padding bands, plus the gutters outside the container on wide screens.
 */
const fullZones: Zone[] = [{
  top: '14px',
  left: '31%',
  rotate: -10,
  size: small,
  vis: 'sm'
}, {
  top: '10px',
  right: '27%',
  rotate: 12,
  size: small,
  vis: 'sm'
}, {
  bottom: '14px',
  left: '19%',
  rotate: 8,
  size: small,
  vis: 'sm'
}, {
  bottom: '10px',
  right: '33%',
  rotate: -12,
  size: small,
  vis: 'sm'
}, {
  top: '18px',
  left: '63%',
  rotate: -16,
  size: small,
  vis: 'lg'
}, {
  bottom: '20px',
  left: '49%',
  rotate: 14,
  size: small,
  vis: 'lg'
},
// Outer gutters: kept just outside the content box but inside the viewport,
// so nothing clips at the xl breakpoint's narrowest width.
{
  top: '17%',
  left: '-5%',
  rotate: -8,
  size: medium,
  vis: 'xl'
}, {
  top: '34%',
  right: '-4.5%',
  rotate: 10,
  size: medium,
  vis: 'xl'
}, {
  top: '57%',
  left: '-4.5%',
  rotate: 16,
  size: small,
  vis: 'xl'
}, {
  top: '74%',
  right: '-5%',
  rotate: -10,
  size: medium,
  vis: 'xl'
}];

/** Centered narrow column: both side gutters are free from `lg` upward. */
const narrowZones: Zone[] = [{
  top: '14px',
  left: '35%',
  rotate: -12,
  size: small,
  vis: 'sm'
}, {
  bottom: '16px',
  right: '37%',
  rotate: 10,
  size: small,
  vis: 'sm'
}, {
  top: '9%',
  left: '1%',
  rotate: -8,
  size: medium,
  vis: 'lg'
}, {
  top: '23%',
  right: '2%',
  rotate: 12,
  size: medium,
  vis: 'lg'
}, {
  top: '41%',
  left: '2.5%',
  rotate: 6,
  size: small,
  vis: 'lg'
}, {
  top: '57%',
  right: '1%',
  rotate: -6,
  size: medium,
  vis: 'lg'
}, {
  top: '73%',
  left: '1%',
  rotate: 16,
  size: small,
  vis: 'lg'
}, {
  top: '86%',
  right: '3%',
  rotate: -10,
  size: medium,
  vis: 'lg'
}, {
  top: '31%',
  right: '9%',
  rotate: 14,
  size: small,
  vis: 'xl'
}, {
  top: '63%',
  left: '9%',
  rotate: -18,
  size: small,
  vis: 'xl'
}];

/**
 * Left-aligned content: the entire right side is dead space, so it gets a dense,
 * larger cluster. This is the case that produced the big empty area on /photos.
 */
const leftColumnZones: Zone[] = [
// A heading can run to ~max-w-3xl, so above the halfway mark shapes stay in
// the far-right sliver only — never in the middle where a long title reaches.
{
  bottom: '14px',
  right: '11%',
  rotate: -10,
  size: small,
  vis: 'always'
}, {
  top: '4%',
  right: '-4%',
  rotate: 12,
  size: medium,
  vis: 'lg'
}, {
  top: '22%',
  right: '-5%',
  rotate: -16,
  size: small,
  vis: 'lg'
}, {
  top: '38%',
  right: '-3%',
  rotate: -6,
  size: large,
  vis: 'lg'
}, {
  top: '46%',
  left: '76%',
  rotate: 18,
  size: small,
  vis: 'lg'
},
// Below the heading block the whole right half is free.
{
  top: '56%',
  right: '13%',
  rotate: -12,
  size: medium,
  vis: 'lg'
}, {
  top: '61%',
  left: '61%',
  rotate: 10,
  size: small,
  vis: 'lg'
}, {
  top: '70%',
  right: '4%',
  rotate: -14,
  size: large,
  vis: 'lg'
}, {
  top: '76%',
  left: '68%',
  rotate: 14,
  size: medium,
  vis: 'lg'
}, {
  top: '86%',
  right: '18%',
  rotate: -8,
  size: small,
  vis: 'lg'
}, {
  top: '90%',
  left: '78%',
  rotate: 6,
  size: medium,
  vis: 'lg'
}];
const layouts: Record<ContentLayout, Zone[]> = {
  full: fullZones,
  narrow: narrowZones,
  leftColumn: leftColumnZones
};
const categories: DoodleCategory[] = ['stars', 'organics', 'strokes', 'stars', 'organics'];
function hash(seed: string): number {
  let value = 7;
  for (let index = 0; index < seed.length; index += 1) {
    value = (value * 31 + seed.charCodeAt(index)) % 100000;
  }
  return value;
}
type DoodleScatterProps = {
  /** The section's background, so shape colors always contrast with it. */
  tone: SurfaceTone;
  /** Unique per section — drives which shapes and positions are chosen. */
  seed: string;
  /** Where the section's content sits, so shapes avoid it. */
  layout?: ContentLayout;
  /** How many shapes to scatter, capped by the layout's free zones. */
  count?: number;
};
export function DoodleScatter({
  tone,
  seed,
  layout = 'full',
  count
}: DoodleScatterProps) {
  const base = hash(seed);
  const families = familiesForTone[tone];
  const zones = layouts[layout];
  const picked = zones.slice(0, Math.min(count ?? zones.length, zones.length));
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden" data-id="element-46" data-mp="3kprh2e7nkmlm">
      {/* Mirrors Container so shape coordinates match the real content box. */}
      <div className="relative mx-auto h-full w-full max-w-6xl px-5 sm:px-8" data-id="element-47" data-mp="3pqxayfd1ufwq">
        {picked.map((zone, index) => {
        const family = families[(base + index * 2) % families.length];
        const category = categories[(base + index * 3) % categories.length];
        const list = doodleCatalog[family][category];
        const src = list[(base + index * 7 + family.length) % list.length];
        return <img key={`${seed}-${index}`} src={src} alt="" aria-hidden="true" draggable={false} decoding="async" style={{
          top: zone.top,
          bottom: zone.bottom,
          left: zone.left,
          right: zone.right,
          transform: `rotate(${zone.rotate}deg)`
        }} className={`absolute select-none object-contain ${zone.size[category]} ${visibilityClass[zone.vis]}`} data-id="element-48" data-mp="1e18v4rv8htdi" data-mpi={index} />;
      })}
      </div>
    </div>;
}